import React, { useCallback, useMemo } from 'react';
import { queryKeys } from '@config/queryKeys';
import strings from '@config/strings';
import { useVendor } from '@context/VendorContext';
import { useGetSiteProfile } from '@features/settings/organization-site/hooks/useGetSiteProfile';
import { dateFormats } from '@goodfynd/strings';
import { ButtonLink, FlexView, Icon, Image, Link, Loading, Tag, Text } from '@goodfynd/ui';
import { ScheduleSort, useScheduleSearch } from '@hooks/useScheduleSearch';
import { dateUtil } from '@utils/date-util';
import { getStaticMapImage } from '@utils/maps-util';
import { createPath, getConsumerUrl } from '@utils/path-util';
import classnames from 'clsx';
import type { ScheduleSearchOptions } from 'types/schedule';

import { OneClickScheduleButton } from '../one-click-schedule';

import type { NextEventBannerProps } from './types';

import styles from './NextEventBanner.module.css';

export function NextEventBanner({
  children,
  className,
  subTitle,
  ...props
}: NextEventBannerProps) {
  const { vendor } = useVendor();
  const request = useMemo<ScheduleSearchOptions['request']>(
    () => ({
      dateStart: new Date().toISOString(),
      rowsPerPage: 1,
      sorts: ScheduleSort.StartAsc,
      vendorIds: vendor.id,
    }),
    [vendor.id]
  );
  const queryKey = useMemo(
    () =>
      queryKeys.schedule.search({
        rows: request.rowsPerPage,
        sorts: request.sorts,
        vendorIds: request.vendorIds,
      }),
    [request]
  );
  const { isFetched, isLoading, schedules } = useScheduleSearch({
    queryKey,
    refetchOnWindowFocus: true,
    request,
  });
  const nextEvent = useMemo(() => schedules[0], [schedules]);
  const location = useMemo(
    () => nextEvent?.pickupLocation ?? nextEvent?.location,
    [nextEvent]
  );
  const nextEventIsToday = useMemo(
    () => dateUtil.isToday(nextEvent?.start),
    [nextEvent]
  );

  const { isLoading: isLoadingInstantSite, siteProfile } = useGetSiteProfile(
    vendor.organizationId,
    'published',
    {
      enabled: !!nextEvent?.id,
    }
  );

  const websiteUrl = useMemo(() => {
    if (!nextEvent?.id) {
      return null;
    }

    return (
      nextEvent?.scheduleUrl ??
      siteProfile?.websiteUrl ??
      getConsumerUrl(createPath('schedules', nextEvent.id))
    );
  }, [siteProfile?.websiteUrl, nextEvent?.id, nextEvent?.scheduleUrl]);

  const renderContent = useCallback(() => {
    if (isLoading || isLoadingInstantSite) {
      return <Loading type="line" />;
    }

    return (
      <>
        {location && (
          <>
            <div className={styles['map-container-mobile']}>
              <Link href={location?.directions} target="_blank">
                <Image
                  alt={`Map of ${location.name}`}
                  height={278}
                  width={900}
                  src={getStaticMapImage(location.coordinates, '900x278', {
                    mapType: 'roadmap',
                  })}
                />
              </Link>
            </div>

            <Link href={location?.directions} target="_blank">
              <Icon
                name="location_pin"
                label={
                  <div>
                    <Text as="body1">{location.name}</Text>

                    <Text inherit>{location.address}</Text>
                  </div>
                }
              />
            </Link>

            <Icon
              name="clock"
              label={
                <Text>
                  {dateUtil.format(
                    new Date(nextEvent.start),
                    dateFormats.weekDayMonthDay
                  )}
                  &nbsp;
                  <span>
                    from&nbsp;
                    {dateUtil.getTimeDisplay(new Date(nextEvent.start))}
                    &nbsp;-&nbsp;
                    {dateUtil.getTimeDisplay(new Date(nextEvent.end))}
                    &nbsp;
                  </span>
                </Text>
              }
            />
          </>
        )}

        <FlexView direction="column" gap={16}>
          {(nextEvent?.isPrivate || nextEvent?.pickupCapacity > 0) && (
            <FlexView align="center" direction="column" gap={4}>
              {nextEvent.isPrivate && (
                <Tag label="PRIVATE" type="accent-purple" />
              )}

              {nextEvent.pickupCapacity > 0 && (
                <Tag label="Accepting online orders" type="positive" />
              )}
            </FlexView>
          )}

          <FlexView gap={16}>
            {websiteUrl && (
              <ButtonLink
                content="View public page"
                href={websiteUrl}
                rank="primary"
                target="_blank"
              />
            )}

            {isFetched && !nextEventIsToday && (
              <OneClickScheduleButton queryKey={queryKey} />
            )}
          </FlexView>
        </FlexView>

        {children}

        {location && (
          <div className={styles['map-container']}>
            <Link href={location?.directions} target="_blank">
              <Image
                alt={`Map of ${location.name}`}
                height={278}
                width={600}
                src={getStaticMapImage(location.coordinates, '600x278', {
                  mapType: 'roadmap',
                })}
              />
            </Link>
          </div>
        )}
      </>
    );
  }, [
    children,
    isFetched,
    isLoading,
    isLoadingInstantSite,
    location,
    nextEvent,
    nextEventIsToday,
    queryKey,
    websiteUrl,
  ]);

  return (
    <section className={classnames(styles.container, className)} {...props}>
      <div>
        {location && !!subTitle && <Text as="body2">{subTitle}</Text>}
        <Text as="heading5">
          {location ? 'Your next event' : strings.titles.noUpcomingEvents}
        </Text>
      </div>

      {renderContent()}
    </section>
  );
}
